/**
 * Resize a single grid item to span the correct number of rows.
 */
const resizeGridItem = (item: HTMLElement) => {
  const grid = item.parentElement;
  if (!grid) return;

  const rowHeight = parseInt(
    window.getComputedStyle(grid).getPropertyValue("grid-auto-rows"),
  );
  const rowGap = parseInt(
    window.getComputedStyle(grid).getPropertyValue("gap"),
  );

  // We look for the inner wrapper to get the exact height of the content
  const content = item.querySelector(
    ".archive-masonry-item__content-wrapper",
  ) as HTMLElement;

  if (!content) return;

  // Use scrollHeight for robust height detection including overflow
  // Add a small buffer (e.g. 0px) just in case sub-pixel rendering cuts off bottom
  const contentHeight = content.getBoundingClientRect().height;

  // Formula: span = ceil( (contentHeight + gap) / (rowHeight + gap) )
  const rowSpan = Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap));

  item.style.gridRowEnd = "span " + rowSpan;
};

/**
 * Loop through all items and resize them.
 */
const resizeAllGridItems = () => {
  const allItems = document.querySelectorAll<HTMLElement>(
    ".archive-masonry-item",
  );
  allItems.forEach((item) => resizeGridItem(item));
};

export const initArchiveMasonry = (): void => {
  const grids = document.querySelectorAll("[data-masonry-grid]");
  if (grids.length === 0) return;

  // 1. Initial calculation
  // We wrap this in a small timeout to let the browser paint first
  setTimeout(resizeAllGridItems, 100);

  // 2. Handle Image Loading
  // Iterate through all images in the grid and listen for load
  const allImages = document.querySelectorAll(".archive-masonry-item img");
  allImages.forEach((img) => {
    if (img instanceof HTMLImageElement && !img.complete) {
      img.addEventListener("load", () => {
        // Find the parent item of this specific image and resize just that one
        const parentItem = img.closest(".archive-masonry-item") as HTMLElement;
        if (parentItem) resizeGridItem(parentItem);
      });
    }
  });

  // 3. Handle Window Resize
  // Debounce resize event for performance
  let resizeTimer: number;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(resizeAllGridItems, 100);
  });

  // 4. Fallback: ResizeObserver
  // If fonts load or other layout shifts occur, this catches it
  const observer = new ResizeObserver((entries) => {
    // Only resize the items that actually changed size
    entries.forEach((entry) => {
      // If the content wrapper changed size, resize its parent item
      const parentItem = entry.target.closest(
        ".archive-masonry-item",
      ) as HTMLElement;
      if (parentItem) resizeGridItem(parentItem);
    });
  });

  // Observe the CONTENT WRAPPER, not the item itself (to avoid infinite loops)
  document
    .querySelectorAll(".archive-masonry-item__content-wrapper")
    .forEach((wrapper) => {
      observer.observe(wrapper);
    });

  console.log(
    "[Masonry] Initialized JS Grid layout with robust image handling.",
  );
};
